import { cva } from 'class-variance-authority';
import { CarWheelMeasurement, WheelSetTireMeasurementV2 } from 'noddi-types';

import { CarSideIcon, CarWheelPositions, CarWheelPositionType } from 'noddi-ui-common';
import { commonTexts, DateFormats, noddiFormatDate } from 'noddi-util';
import {
  getSummerTireStatus,
  getWinterTireStatus,
  NoddiBasicCard,
  resolveWheelSetStatusLogo,
  wheelSetStatusToTranslation
} from '../../../..';
import { TireType, WheelSetStatus } from '../types';

interface TireMeasurementTranslations {
  leftFront: string;
  rightFront: string;
  leftBack: string;
  rightBack: string;
  summerTires: string;
  winterTires: string;
  measuredAt: string;
  inAGoodCondition: string;
  missingMeasurements: string;
  soonWornOut: string;
  wornOut: string;
}

interface TireMeasurementCardProps {
  tireType: TireType;
  translations?: TireMeasurementTranslations;
  carName?: string;
  measurements: WheelSetTireMeasurementV2;
  hideCarName?: boolean;
}

export const TireMeasurementCard = ({
  tireType,
  translations,
  carName,
  measurements,
  hideCarName
}: TireMeasurementCardProps) => {
  const safeGuardTranslations = translations ?? commonTexts.wheelMeasurements;
  return (
    <NoddiBasicCard className='flex flex-col gap-4'>
      {!hideCarName && (
        <div className='flex flex-wrap justify-between gap-4'>
          {carName && <p className='font-bold text-primary'>{carName}</p>}
          {tireType === 'winter' ? (
            <span> {safeGuardTranslations.winterTires} ❄️</span>
          ) : (
            <span> {safeGuardTranslations.summerTires} ☀️</span>
          )}
        </div>
      )}
      <div className='grid grid-cols-2 gap-4'>
        <Measurement
          tireType={tireType}
          position={CarWheelPositions.FRONT_LEFT}
          positionText={safeGuardTranslations.leftFront}
          measurement={measurements.frontLeft}
          translations={safeGuardTranslations}
        />
        <Measurement
          tireType={tireType}
          position={CarWheelPositions.FRONT_RIGHT}
          positionText={safeGuardTranslations.rightFront}
          measurement={measurements.frontRight}
          translations={safeGuardTranslations}
        />
        <Measurement
          tireType={tireType}
          position={CarWheelPositions.REAR_LEFT}
          positionText={safeGuardTranslations.leftBack}
          measurement={measurements.rearLeft}
          translations={safeGuardTranslations}
        />
        <Measurement
          tireType={tireType}
          position={CarWheelPositions.REAR_RIGHT}
          positionText={safeGuardTranslations.rightBack}
          measurement={measurements.rearRight}
          translations={safeGuardTranslations}
        />
      </div>
      <p className='mt-1'>
        {safeGuardTranslations.measuredAt +
          ` ${noddiFormatDate(measurements.frontLeft.measuredAt, DateFormats.DOTTED_DATE)}`}
      </p>
    </NoddiBasicCard>
  );
};

const borderStyles = cva('', {
  variants: {
    variant: {
      [WheelSetStatus.Satisfactory]: '',
      [WheelSetStatus.MissingMeasurements]: '',
      [WheelSetStatus.SoonWornOut]: 'border border-warning',
      [WheelSetStatus.WornOut]: 'border border-danger'
    }
  },
  defaultVariants: {
    variant: WheelSetStatus.Satisfactory
  }
});

const textStyles = cva('', {
  variants: {
    variant: {
      [WheelSetStatus.Satisfactory]: '',
      [WheelSetStatus.MissingMeasurements]: '',
      [WheelSetStatus.SoonWornOut]: 'text-warning',
      [WheelSetStatus.WornOut]: 'text-danger'
    }
  },
  defaultVariants: {
    variant: WheelSetStatus.Satisfactory
  }
});

interface MeasurementProps {
  measurement: CarWheelMeasurement;
  tireType: TireType;
  positionText: string;
  translations: TireMeasurementTranslations;
  position: CarWheelPositionType;
}

function Measurement({ measurement, tireType, positionText, translations, position }: MeasurementProps) {
  const wheelStatus =
    tireType === 'summer' ? getSummerTireStatus(measurement.value.value) : getWinterTireStatus(measurement.value.value);

  const isSatisfactory = wheelStatus === WheelSetStatus.Satisfactory;
  const isMissingMeasurements = wheelStatus === WheelSetStatus.MissingMeasurements;

  const borderStyle = borderStyles({ variant: wheelStatus });
  const textStyle = textStyles({ variant: wheelStatus });

  return (
    <div className={`flex flex-col gap-1 rounded-lg bg-darkPurpleGrey p-2 ${borderStyle}`}>
      <div className='flex flex-row items-center gap-2 py-1'>
        <div>
          <CarSideIcon carSide={position} />
        </div>

        <p>{positionText}</p>
      </div>

      <div className='flex items-center gap-2'>
        {/* only show icon if not satisfactory */}
        <div className='flex flex-col md:flex-row md:gap-4'>
          <p className='font-bold'>{measurement.value.value} mm</p>
          {!isSatisfactory && !isMissingMeasurements && (
            <div className='flex gap-1'>
              <div className='-ml-1'>{resolveWheelSetStatusLogo(wheelStatus)}</div>
              <p className={textStyle}>{wheelSetStatusToTranslation(wheelStatus, translations)}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
